/**
 * textosDaLigacao — as cópias do registro de uma ligação no chat (Task 10, 24/10/2026).
 *
 * `textoDoCartao` é o texto do `CartaoDeLigacao` (Task 12), tal como o desenho
 * (`docs/superpowers/specs/2026-10-22-ligacoes-do-chat-tela.md`, §3.2) e o brief da Task 12 o
 * fixam: voz por padrão, e "de vídeo" no lugar de "de voz" quando a mídia é vídeo. A hora NÃO
 * entra: o balão da mensagem já a imprime no meta.
 *
 * `textoDaPrevia` é a mesma língua SEM ponto de vista, para as prévias do registro fora da bolha (o "Respondendo a"
 * do compositor, a barra de fixadas, o filtro de hashtag — A6, 25/10/2026): lida por qualquer um, e nas fixadas o
 * autor já aparece ao lado. Por isso "perdida", "recusada" e "iniciada" valem para quem ligou e para quem recebeu.
 *
 * Nunca devolve vazio nem `null`/`undefined`/`NaN`: o `call_event` vem do servidor e cresce (evento
 * novo, mensagem antiga sem `media`, `duration_seconds` nulo num `ended`).
 */

import type { Midia } from "@/types/calls";

/**
 * O `title` dos botões que ligam (o avatar do organograma, o 📞/📹 do cabeçalho do chat) enquanto estão desligados
 * porque a pessoa já está numa ligação (A7): um estado passageiro dela, não falta de permissão — o botão diz por quê.
 */
export const TITULO_JA_EM_LIGACAO = "Você já está numa ligação";

/** `45 s`, `12 min`, `1 h 05 min`. Segundo quebrado é cortado; negativo ou inválido vira `0 s`. */
export function duracaoDaLigacao(segundos: number): string {
  const total = Number.isFinite(segundos) && segundos > 0 ? Math.floor(segundos) : 0;
  if (total < 60) return `${total} s`;
  const minutos = Math.floor(total / 60);
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return `${horas} h ${String(minutos % 60).padStart(2, "0")} min`;
}

/** "Chamada de voz · 12 min"; sem duração válida (nula, `NaN`, negativa), só "Chamada de voz". */
function comDuracao(chamada: string, duracaoSegundos: number | null | undefined): string {
  return typeof duracaoSegundos === "number" &&
    Number.isFinite(duracaoSegundos) &&
    duracaoSegundos >= 0
    ? `${chamada} · ${duracaoDaLigacao(duracaoSegundos)}`
    : chamada;
}

export interface ArgsDoTextoDoCartao {
  /** `started`, `ended`, `missed` ou `declined`; qualquer outro (`joined`, `left`, novo) vira "Chamada". */
  evento: string;
  /** Ausente (mensagem antiga) vale voz. */
  midia?: "audio" | "video" | null;
  /** O autor do cartão é a pessoa que ligou. */
  souAutor: boolean;
  nomeDoAutor: string;
  /** Só o `ended` usa. Nulo (ou inválido) = sem duração no texto. */
  duracaoSegundos?: number | null;
}

/** O texto do cartão da ligação. Ver o cabeçalho do arquivo para as regras. */
export function textoDoCartao({
  evento,
  midia,
  souAutor,
  nomeDoAutor,
  duracaoSegundos,
}: ArgsDoTextoDoCartao): string {
  const tipo = midia === "video" ? "vídeo" : "voz";
  const chamada = `Chamada de ${tipo}`;
  const umaChamada = `uma chamada de ${tipo}`;

  switch (evento) {
    case "ended":
      return comDuracao(chamada, duracaoSegundos);
    case "missed":
      return souAutor ? `${chamada} não atendida` : `${chamada} perdida`;
    case "declined":
      return souAutor ? `${chamada} recusada` : `Você recusou ${umaChamada}`;
    case "started": {
      if (souAutor) return `Você começou ${umaChamada}`;
      const nome = typeof nomeDoAutor === "string" ? nomeDoAutor.trim() : "";
      // Sem nome não há frase com sujeito: cai no texto genérico em vez de imprimir "undefined".
      return nome ? `${nome} começou ${umaChamada}` : chamada;
    }
    default:
      return "Chamada";
  }
}

/** O `content` de um `call_event` como a prévia o lê: tudo opcional, porque vem do servidor e cresce. */
export interface ConteudoDaPrevia {
  event?: string;
  media?: Midia | null;
  duration_seconds?: number | null;
}

/** A prévia de uma linha só do registro da ligação. Ver o cabeçalho do arquivo para as regras. */
export function textoDaPrevia(content: ConteudoDaPrevia | null | undefined): string {
  const chamada = `Chamada de ${content?.media === "video" ? "vídeo" : "voz"}`;

  switch (content?.event) {
    case "ended":
      return comDuracao(chamada, content.duration_seconds);
    case "missed":
      return `${chamada} perdida`;
    case "declined":
      return `${chamada} recusada`;
    case "started":
      return `${chamada} iniciada`;
    default:
      return "Chamada";
  }
}
